import * as React from "react";
import {observer} from "mobx-react";
import moment, {Moment} from "moment";
import {DatePicker} from "antd";
import {dataStore} from "src/models/DataStore";
import {injectIntl, InjectedIntlProps, FormattedMessage} from "react-intl";
import {
    Chart,
    LineAdvance,
} from "bizcharts";
import SwitchRegion from "../../SwitchRegion";

const {RangePicker} = DatePicker;

export type WhiteboardDataProps = InjectedIntlProps;
export type WhiteboardDataStates = {
    startDay: number | null;
    endDay: number | null;
    searchText: any;
};
export type WhiteboardDuration = {
    duration: number;
    date: string;
};
export type WhiteboardPeak = {
    peak: number;
    date: string;
};

@observer
class WhiteboardData extends React.Component<WhiteboardDataProps, WhiteboardDataStates> {
    public constructor(props: WhiteboardDataProps) {
        super(props);
        this.state = {
            startDay: null,
            endDay: null,
            searchText: "",
        };
    }

    private handleRangePicker = async (dates: [Moment, Moment]): Promise<void> => {
        const startDay = dates[0].startOf("day").valueOf();
        const endDay = dates[1].startOf("day").valueOf();
        await dataStore.getTeamWhiteboardData(startDay, endDay);
        this.setState({startDay: startDay, endDay: endDay});
    }

    public async componentDidMount(): Promise<void> {
        const timestamp = new Date().getTime();
        const endDay = moment(timestamp).utcOffset(8).startOf("day").valueOf();
        const startDay = moment(timestamp).subtract(7, "d").utcOffset(8).startOf("day").valueOf();
        await dataStore.getTeamWhiteboardData(startDay, endDay);
    }

    public render(): React.ReactNode {
        const {intl} = this.props;
        let writableDayDataArray: WhiteboardDuration[] = [];
        let writableDayPeakArray: WhiteboardPeak[] = [];
        let readonlyDayDataArray: WhiteboardDuration[] = [];
        let readonlyDayPeakArray: WhiteboardPeak[] = [];
        if (dataStore.team_duration.length > 0) {
            writableDayDataArray = dataStore.team_duration.map(data => {
                return {
                    date: moment(data.timestamp).format("MMM Do"),
                    duration: data.writableDuration,
                    type: intl.formatMessage({id: "dash.whiteboard-writable-duration"}),
                };
            });
            writableDayPeakArray = dataStore.team_duration.map(data => {
                return {
                    date: moment(data.timestamp).format("MMM Do"),
                    peak: data.writablePeak,
                    type: intl.formatMessage({id: "dash.whiteboard-writable-peak"}),
                };
            });
            readonlyDayDataArray = dataStore.team_duration.map(data => {
                return {
                    date: moment(data.timestamp).format("MMM Do"),
                    duration: data.readonlyDuration,
                    type: intl.formatMessage({id: "dash.whiteboard-readonly-duration"}),
                };
            });
            readonlyDayPeakArray = dataStore.team_duration.map(data => {
                return {
                    date: moment(data.timestamp).format("MMM Do"),
                    peak: data.readonlyPeak,
                    type: intl.formatMessage({id: "dash.whiteboard-readonly-peak"}),
                };
            });
        }
        return (
            <div className="product-center-box">
                <div className="portal-room-outbox">
                    <div className="portal-room-chart">
                        <div className="portal-room-box-mid">
                            <div className="console-content-chart-body">
                                <div className="console-content-chart-box-tips">
                                    <div className="clipboard-innerbox-text-mid">
                                        <FormattedMessage id={"whiteboard-usage-trends"} />
                                        <div>
                                            <span className="clipboard-innerbox-text-region">
                                                <SwitchRegion onChange={() => this.componentDidMount()} />
                                            </span>
                                            <RangePicker onChange={this.handleRangePicker} style={{marginRight: 20}}/>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="portal-room-chart">
                        <div className="portal-room-box-mid">
                            <div className="console-content-title-max">
                                <span className="console-content-title-text">
                                    <FormattedMessage
                                        id="dash.whiteboard-writable-duration"
                                    />
                                </span>
                            </div>
                            <div className="console-content-chart-body">
                                <div className="console-content-chart-box">
                                    <div className="console-service-text">
                                        <FormattedMessage
                                            id="global.total"
                                        />
                                    </div>
                                    <div className="console-service-add">{dataStore.writableDurationDayAdded}
                                        <FormattedMessage
                                            id="global.minute"
                                        />
                                    </div>
                                    <Chart autoFit
                                           padding={[20, 120, 96, 120]}
                                           width={1200}
                                           height={300}
                                           data={writableDayDataArray}>
                                        <LineAdvance
                                            shape="smooth"
                                            point
                                            area
                                            position="date*duration"
                                            color={["type", ["#3381FF"]]}
                                        />
                                    </Chart>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="portal-room-chart">
                        <div className="portal-room-box-mid">
                            <div className="console-content-title-max">
                                <span className="console-content-title-text">
                                    <FormattedMessage
                                        id="dash.whiteboard-writable-peak"
                                    />
                                </span>
                            </div>
                            <div className="console-content-chart-body">
                                <div className="console-content-chart-box">
                                    <div className="console-service-text">
                                        <FormattedMessage
                                            id="global.total"
                                        />
                                    </div>
                                    <div className="console-service-add">{dataStore.writablePeakDayAdded}
                                        <FormattedMessage
                                            id="global.minute"
                                        /></div>
                                    <Chart autoFit
                                           padding={[20, 120, 96, 120]}
                                           width={1200}
                                           height={300}
                                           data={writableDayPeakArray}>
                                        <LineAdvance
                                            shape="smooth"
                                            point
                                            area
                                            position="date*peak"
                                            color={["type", ["#3381FF"]]}
                                        />
                                    </Chart>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="portal-room-chart">
                        <div className="portal-room-box-mid">
                            <div className="console-content-title-max">
                                <span className="console-content-title-text">
                                    <FormattedMessage
                                        id="dash.whiteboard-readonly-duration"
                                    />
                                </span>
                            </div>
                            <div className="console-content-chart-body">
                                <div className="console-content-chart-box">
                                    <div className="console-service-text">
                                        <FormattedMessage
                                            id="global.total"
                                        />
                                    </div>
                                    <div className="console-service-add">{dataStore.readonlyDurationDayAdded}
                                        <FormattedMessage
                                            id="global.minute"
                                        /></div>
                                    <Chart autoFit
                                           padding={[20, 120, 96, 120]}
                                           width={1200}
                                           height={300}
                                           data={readonlyDayDataArray}>
                                        <LineAdvance
                                            shape="smooth"
                                            point
                                            area
                                            position="date*duration"
                                            color={["type", ["#3381FF"]]}
                                        />
                                    </Chart>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="portal-room-chart">
                        <div className="portal-room-box-mid">
                            <div className="console-content-title-max">
                                <span className="console-content-title-text">
                                    <FormattedMessage
                                        id="dash.whiteboard-readonly-peak"
                                    />
                                </span>
                            </div>
                            <div className="console-content-chart-body">
                                <div className="console-content-chart-box">
                                    <div className="console-service-text">
                                        <FormattedMessage
                                            id="global.total"
                                        />
                                    </div>
                                    <div className="console-service-add">{dataStore.readonlyPeakDayAdded}
                                        <FormattedMessage
                                            id="global.minute"
                                        />
                                    </div>
                                    <Chart autoFit
                                           padding={[20, 120, 96, 120]}
                                           width={1200}
                                           height={300}
                                           data={readonlyDayPeakArray}>
                                        <LineAdvance
                                            shape="smooth"
                                            point
                                            area
                                            position="date*peak"
                                            color={["type", ["#3381FF"]]}
                                        />
                                    </Chart>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

}

export default injectIntl(WhiteboardData);





// WEBPACK FOOTER //
// ./src/components/portal/dataCenter/WhiteboardData.tsx